import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Paperclip, Send, Trash2, X } from "lucide-react";
import { lazy, Suspense, useState, type ChangeEvent, type JSX, type KeyboardEvent } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Label,
  Spinner,
  Skeleton,
  useToast,
} from "@mailflare/ui";
import type {
  AddressObject,
  Attachment,
  DraftId,
  MailboxWithCounts,
  MessageId,
  UserPublic,
} from "@mailflare/shared";

import { queryKeys } from "@/lib/query-keys";
import { formatBytes, htmlToText } from "@/lib/utils";
import { createDraft } from "@/features/compose/api";
import { uploadAttachment } from "@/features/messages/attachments-api";
import { forwardMessage, replyToMessage, sendMessage } from "@/features/messages/api";
import { useComposeStore, type ComposeMode } from "@/stores/compose-store";

import { RecipientsField } from "./recipients-field";

const RichTextEditor = lazy(() => import("./rich-text-editor"));

const TITLES: Record<ComposeMode, string> = {
  new: "New message",
  reply: "Reply",
  reply_all: "Reply all",
  forward: "Forward",
};

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Message could not be sent.";
}

interface ComposeFormProps {
  readonly mailbox: MailboxWithCounts;
  readonly user: UserPublic;
  readonly mode: ComposeMode;
  readonly sourceMessageId: string | null;
  readonly prefill: ReturnType<typeof useComposeStore.getState>["prefill"];
  readonly onClose: () => void;
}

function ComposeForm({
  mailbox,
  user,
  mode,
  sourceMessageId,
  prefill,
  onClose,
}: ComposeFormProps): JSX.Element {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [to, setTo] = useState<AddressObject[]>(() => [...(prefill?.to ?? [])]);
  const [cc, setCc] = useState<AddressObject[]>(() => [...(prefill?.cc ?? [])]);
  const [bcc, setBcc] = useState<AddressObject[]>(() => [...(prefill?.bcc ?? [])]);
  const [subject, setSubject] = useState(prefill?.subject ?? "");
  const [html, setHtml] = useState(prefill?.html ?? "");
  const [attachments, setAttachments] = useState<Attachment[]>(() => [
    ...(prefill?.attachments ?? []),
  ]);
  const [showCc, setShowCc] = useState(
    (prefill?.cc?.length ?? 0) > 0 || (prefill?.bcc?.length ?? 0) > 0,
  );
  const [uploading, setUploading] = useState(false);
  const isReply = mode === "reply" || mode === "reply_all";
  const canSend = to.length > 0 && !uploading;

  const send = useMutation({
    mutationFn: async () => {
      const text = htmlToText(html);
      const attachmentIds = attachments.map((attachment) => attachment.id);

      if (isReply) {
        if (sourceMessageId === null) throw new Error("Missing message to reply to.");
        return replyToMessage(sourceMessageId as MessageId, {
          replyAll: mode === "reply_all",
          attachmentIds,
          ...(text.length > 0 ? { text } : {}),
          ...(html.length > 0 ? { html } : {}),
        });
      }
      if (mode === "forward" && sourceMessageId !== null) {
        return forwardMessage(sourceMessageId as MessageId, {
          to,
          cc,
          bcc,
          attachmentIds,
          ...(text.length > 0 ? { text } : {}),
          ...(html.length > 0 ? { html } : {}),
        });
      }
      return sendMessage({
        mailboxId: mailbox.id,
        to,
        cc,
        bcc,
        subject,
        attachmentIds,
        references: [...(prefill?.references ?? [])],
        ...(text.length > 0 ? { text } : {}),
        ...(html.length > 0 ? { html } : {}),
        ...(prefill?.inReplyTo !== undefined ? { inReplyTo: prefill.inReplyTo } : {}),
        ...(prefill?.draftId !== undefined ? { draftId: prefill.draftId as DraftId } : {}),
      });
    },
    onSuccess: () => {
      toast({ title: "Message sent", variant: "success" });
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages(mailbox.id) });
      onClose();
    },
    onError: (error) => {
      toast({ title: "Send failed", description: errorMessage(error), variant: "destructive" });
    },
  });

  const saveDraft = useMutation({
    mutationFn: () =>
      createDraft({
        mailboxId: mailbox.id,
        to,
        cc,
        bcc,
        subject,
        text: htmlToText(html),
        html: html.length > 0 ? html : null,
        attachmentIds: attachments.map((attachment) => attachment.id),
      }),
    onSuccess: () => {
      toast({ title: "Draft saved", variant: "success" });
      void queryClient.invalidateQueries({ queryKey: queryKeys.drafts });
      onClose();
    },
    onError: (error) => {
      toast({
        title: "Could not save draft",
        description: errorMessage(error),
        variant: "destructive",
      });
    },
  });

  const handleFiles = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded: Attachment[] = [];
      for (const file of files) {
        uploaded.push(await uploadAttachment(file));
      }
      setAttachments((current) => [...current, ...uploaded]);
    } catch (error) {
      toast({
        title: "Attachment upload failed",
        description: errorMessage(error),
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && canSend && !send.isPending) {
      event.preventDefault();
      send.mutate();
    }
  };

  return (
    <div onKeyDown={handleKeyDown} className="flex flex-col gap-3">
      <RecipientsField
        id="compose-to"
        label="To"
        value={to}
        onChange={setTo}
        placeholder={`${user.email} — type an address and press Enter`}
      />

      {showCc ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <RecipientsField
            id="compose-cc"
            label="Cc"
            value={cc}
            onChange={setCc}
            placeholder="name@example.com"
          />
          <RecipientsField
            id="compose-bcc"
            label="Bcc"
            value={bcc}
            onChange={setBcc}
            placeholder="name@example.com"
          />
        </div>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto self-start p-0 text-xs hover:bg-transparent"
          onClick={() => setShowCc(true)}
        >
          Add Cc / Bcc
        </Button>
      )}

      {!isReply ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="compose-subject">Subject</Label>
          <Input
            id="compose-subject"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
          />
        </div>
      ) : null}

      <Suspense fallback={<Skeleton className="h-40 w-full" />}>
        <RichTextEditor initialHtml={html} onChange={setHtml} />
      </Suspense>

      {attachments.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label="Attachments">
          {attachments.map((attachment) => (
            <li
              key={attachment.id}
              className="bg-muted flex items-center gap-2 rounded-md px-2 py-1 text-xs"
            >
              <Paperclip aria-hidden className="size-3" />
              <span className="max-w-48 truncate">{attachment.filename}</span>
              <span className="text-muted-foreground">{formatBytes(attachment.sizeBytes)}</span>
              <button
                type="button"
                aria-label={`Remove ${attachment.filename}`}
                onClick={() =>
                  setAttachments((current) => current.filter((item) => item.id !== attachment.id))
                }
                className="focus-visible:ring-ring rounded focus-visible:ring-2 focus-visible:outline-none"
              >
                <X aria-hidden className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="border-border flex flex-wrap items-center gap-2 border-t pt-3">
        <Button
          onClick={() => send.mutate()}
          disabled={!canSend || send.isPending}
          data-testid="send-button"
        >
          {send.isPending ? (
            <Spinner aria-hidden className="size-4" />
          ) : (
            <Send aria-hidden className="size-4" />
          )}
          Send
        </Button>
        <label className="border-input bg-background hover:bg-accent hover:text-accent-foreground inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border px-4 text-sm font-medium shadow-xs">
          <Paperclip aria-hidden className="size-4" />
          Attach
          <input
            type="file"
            multiple
            className="sr-only"
            onChange={(event) => void handleFiles(event)}
            disabled={uploading}
          />
        </label>
        {uploading ? <Spinner label="Uploading attachment" className="size-4" /> : null}
        <Button
          variant="ghost"
          onClick={() => saveDraft.mutate()}
          disabled={saveDraft.isPending}
          className="ml-auto"
        >
          <Trash2 aria-hidden className="size-4" />
          Save draft
        </Button>
      </div>
      {!canSend ? (
        <p className="text-muted-foreground text-xs">Add at least one recipient to send.</p>
      ) : null}
    </div>
  );
}

export interface ComposeModalProps {
  readonly mailbox: MailboxWithCounts | null;
  readonly user: UserPublic;
}

export function ComposeModal({ mailbox, user }: ComposeModalProps): JSX.Element | null {
  const open = useComposeStore((state) => state.open);
  const mode = useComposeStore((state) => state.mode);
  const prefill = useComposeStore((state) => state.prefill);
  const sourceMessageId = useComposeStore((state) => state.sourceMessageId);
  const closeCompose = useComposeStore((state) => state.closeCompose);

  if (!open || mailbox === null) return null;

  return (
    <Dialog open onOpenChange={(next) => (!next ? closeCompose() : undefined)}>
      <DialogContent className="max-h-[90dvh] max-w-2xl overflow-y-auto">
        <DialogTitle>{TITLES[mode]}</DialogTitle>
        <DialogDescription className="sr-only">
          Compose a message. Press Control or Command plus Enter to send.
        </DialogDescription>
        <ComposeForm
          mailbox={mailbox}
          user={user}
          mode={mode}
          sourceMessageId={sourceMessageId}
          prefill={prefill}
          onClose={closeCompose}
        />
      </DialogContent>
    </Dialog>
  );
}
